import React, { useState } from "react";
import styled from "styled-components";
import { SupplyOrder } from "../store/supplyStore";

const ExpandedOrderRow = styled.div`
  background-color: rgba(255, 255, 255, 0.02);
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const ProductsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProductImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  background-color: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const ProductArticle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #8ca9a9;
`;

const ProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 16px;
  }
`;

const ProductMetaItem = styled.div`
  font-size: 14px;
  color: #8ca9a9;
`;

const SizesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SizesHeader = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SizesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SizeTag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background-color: rgba(36, 185, 125, 0.08);
  border: 1px solid rgba(36, 185, 125, 0.3);
  border-radius: 8px;
  min-width: 70px;
  gap: 2px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(36, 185, 125, 0.12);
    border-color: rgba(36, 185, 125, 0.5);
    transform: translateY(-1px);
  }
`;

const SizeLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #24b97d;
`;

const SizeDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const SizeQuantity = styled.div`
  font-size: 11px;
  color: #8ca9a9;
  font-weight: 500;
`;

const SizePrice = styled.div`
  font-size: 12px;
  color: #ffffff;
  font-weight: 600;
`;

const DeliveryInfo = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    padding: 16px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0;
  }
`;

const DeliveryText = styled.div`
  color: #10B981;
  font-size: 14px;
  font-weight: 500;
`;

const DeliveryButton = styled.button`
  padding: 12px 16px;
  background-color: #10B981;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 44px;

  @media (min-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
    width: auto;
    min-height: auto;
  }

  &:hover {
    background-color: #059669;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TrackingInfo = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    padding: 16px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0;
  }
`;

const TrackingText = styled.div`
  color: #3B82F6;
  font-size: 14px;
  font-weight: 500;
`;

const TrackingButton = styled.button`
  padding: 12px 16px;
  background-color: #3B82F6;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 44px;

  @media (min-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
    width: auto;
    min-height: auto;
  }

  &:hover {
    background-color: #2563EB;
  }

  &:active {
    transform: scale(0.95);
  }
`;

interface OrderDetailRowProps {
  order: SupplyOrder;
}

const OrderDetailRow: React.FC<OrderDetailRowProps> = ({ order }) => {
  // Группируем товары по артикулу
  const groupItemsByArticle = () => {
    const items = order.items || [];
    const grouped: { [key: string]: Array<{
      article: string;
      size_eu: number;
      price: number;
      count: number;
      model?: string;
      color?: string;
      image_url?: string;
    }> } = {};
    
    items.forEach(item => {
      if (!grouped[item.article]) {
        grouped[item.article] = [];
      }
      grouped[item.article].push(item);
    });
    
    return grouped;
  };

  const calculateProductTotal = (items: Array<{ price: number; count: number }>) => {
    return items.reduce((total, item) => total + (item.price * item.count), 0);
  };

  const calculateProductQuantity = (items: Array<{ count: number }>) => {
    return items.reduce((total, item) => total + item.count, 0);
  };

  const renderStatusAction = () => {
    switch (order.status) {
      case 'confirmed':
        return (
          <DeliveryInfo>
            <DeliveryText>Частично готов</DeliveryText>
            <DeliveryButton>Доставлен</DeliveryButton>
          </DeliveryInfo>
        );
      case 'shipped':
        return (
          <TrackingInfo>
            <TrackingText>В пути</TrackingText>
            <TrackingButton>Отследить</TrackingButton>
          </TrackingInfo>
        );
      default:
        return null;
    }
  };

  const groupedItems = groupItemsByArticle();

  return (
    <ExpandedOrderRow>
      <ProductsGrid>
        {Object.entries(groupedItems).map(([article, items]) => {
          const firstItem = items[0];
          const productTotal = calculateProductTotal(items);
          const productQuantity = calculateProductQuantity(items);
          
          return (
            <ProductCard key={article}>
              <ProductHeader>
                {firstItem.image_url && (
                  <ProductImage 
                    src={firstItem.image_url} 
                    alt={`${firstItem.model} ${firstItem.color}`}
                  />
                )}
                <ProductInfo>
                  <ProductTitle>{firstItem.model} {firstItem.color}</ProductTitle>
                  <ProductArticle>Артикул: {article}</ProductArticle>
                  <ProductMeta>
                    <ProductMetaItem>Количество: {productQuantity} шт.</ProductMetaItem>
                    <ProductMetaItem>Сумма: {productTotal.toLocaleString()} ¥</ProductMetaItem>
                  </ProductMeta>
                </ProductInfo>
              </ProductHeader>
              
              <SizesContainer>
                <SizesHeader>Размеры и цены</SizesHeader>
                <SizesGrid>
                  {items.map((item, idx) => (
                    <SizeTag key={idx}>
                      <SizeLabel>EU {item.size_eu}</SizeLabel>
                      <SizeDetails>
                        <SizeQuantity>{item.count} шт.</SizeQuantity>
                        <SizePrice>{item.price.toLocaleString()} ¥</SizePrice>
                      </SizeDetails>
                    </SizeTag>
                  ))}
                </SizesGrid>
              </SizesContainer>
            </ProductCard>
          );
        })}
      </ProductsGrid>
      
      {renderStatusAction()}
    </ExpandedOrderRow>
  );
};

export default OrderDetailRow;
